import * as React from 'react';
import clsx from 'clsx';
import { useForm, UseFormRegister } from 'react-hook-form';
import { Menu } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import {
	Dropdown,
	GenericBadge,
	Icon,
	Spinner,
	Typography,
} from '@/components/common';
import { InputText } from '@/components/form';
import Icons from '@/const/icons';
import {
	DATA_TYPE_GROUPS,
	DATA_TYPE_LABELS,
	DataType,
	DetectedPdfField,
	FieldConfig,
} from '@/interfaces';

export interface FieldsPanelProps {
	detectedFields: DetectedPdfField[];
	fieldConfigs: Record<string, FieldConfig>;
	selectedFieldId: string | null;
	onSelectField: (fieldId: string) => void;
	onDataTypeChange: (fieldId: string, dataType: DataType) => void;
	onRenameChange: (fieldId: string, newLabel: string) => void;
}

interface PageGroup {
	pageIndex: number;
	fields: DetectedPdfField[];
}

const groupFieldsByPage = (fields: DetectedPdfField[]): PageGroup[] => {
	const groups = new Map<number, DetectedPdfField[]>();

	fields.forEach((field) => {
		const bucket = groups.get(field.pageIndex) ?? [];
		bucket.push(field);
		groups.set(field.pageIndex, bucket);
	});

	return Array.from(groups.entries())
		.sort(([a], [b]) => a - b)
		.map(([pageIndex, pageFields]) => ({ pageIndex, fields: pageFields }));
};

export const FieldsPanel: React.FC<FieldsPanelProps> = ({
	detectedFields,
	fieldConfigs,
	selectedFieldId,
	onSelectField,
	onDataTypeChange,
	onRenameChange,
}) => {
	const { register } = useForm();

	const cardRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());

	const setCardRef = React.useCallback(
		(fieldId: string, node: HTMLDivElement | null) => {
			if (node) cardRefs.current.set(fieldId, node);
			else cardRefs.current.delete(fieldId);
		},
		[]
	);

	React.useEffect(() => {
		if (!selectedFieldId) return;
		const node = cardRefs.current.get(selectedFieldId);
		node?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
	}, [selectedFieldId]);

	const pageGroups = React.useMemo(
		() => groupFieldsByPage(detectedFields),
		[detectedFields]
	);

	if (pageGroups.length === 0) {
		return (
			<div className="flex-1 min-h-0 overflow-y-auto flex flex-col items-center justify-center gap-3 px-5 text-center">
				<Spinner />
				<Typography type="regular-12" className="text-[#6E6A73]">
					Detecting fields…
				</Typography>
			</div>
		);
	}

	return (
		<div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-5 px-5 py-4">
			{pageGroups.map((group) => (
				<PageGroup
					key={group.pageIndex}
					pageIndex={group.pageIndex}
					fields={group.fields}
					fieldConfigs={fieldConfigs}
					selectedFieldId={selectedFieldId}
					onSelectField={onSelectField}
					setCardRef={setCardRef}
					register={register}
					onDataTypeChange={onDataTypeChange}
					onRenameChange={onRenameChange}
				/>
			))}
		</div>
	);
};

interface PageGroupProps {
	pageIndex: number;
	fields: DetectedPdfField[];
	fieldConfigs: Record<string, FieldConfig>;
	selectedFieldId: string | null;
	onSelectField: (fieldId: string) => void;
	setCardRef: (fieldId: string, node: HTMLDivElement | null) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	register: UseFormRegister<any>;
	onDataTypeChange: (fieldId: string, dataType: DataType) => void;
	onRenameChange: (fieldId: string, newLabel: string) => void;
}

const PageGroup: React.FC<PageGroupProps> = ({
	pageIndex,
	fields,
	fieldConfigs,
	selectedFieldId,
	onSelectField,
	setCardRef,
	register,
	onDataTypeChange,
	onRenameChange,
}) => {
	const completedCount = fields.filter(
		(field) => fieldConfigs[field.id]?.dataType !== 'DO_NOT_VALIDATE'
	).length;

	const selectedIndexInPage = selectedFieldId
		? fields.findIndex((field) => field.id === selectedFieldId)
		: -1;
	const selectedPosition =
		selectedIndexInPage >= 0 ? selectedIndexInPage + 1 : null;
	const isPageComplete = fields.length > 0 && completedCount === fields.length;

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-center gap-2">
				<Typography
					type="semibold-12"
					className="!text-[10.5px] uppercase tracking-[0.025em] !text-[#8039DF] shrink-0"
				>
					{`Page ${pageIndex + 1}`}
				</Typography>
				<div className="flex-1 border-b border-[rgba(13,6,22,0.06)]" />
				<GenericBadge
					label={`${completedCount}/${fields.length}`}
					colorStatus={isPageComplete ? 'green' : 'orange'}
					typographyType="semibold-12"
					classNameContainer={clsx(
						'!rounded-full !py-1 !px-2.5 [&_p]:!text-[10px] shrink-0',
						isPageComplete ? '!bg-[#40BB7126]' : '!bg-[rgba(253,152,52,0.15)]'
					)}
				/>
			</div>

			<div className="flex flex-col gap-2">
				{fields.map((field) => (
					<FieldCard
						key={field.id}
						field={field}
						config={fieldConfigs[field.id]}
						isSelected={selectedFieldId === field.id}
						selectedPosition={selectedPosition}
						totalFieldsCount={fields.length}
						onSelect={() => onSelectField(field.id)}
						setCardRef={setCardRef}
						register={register}
						onDataTypeChange={onDataTypeChange}
						onRenameChange={onRenameChange}
					/>
				))}
			</div>
		</div>
	);
};

interface FieldCardProps {
	field: DetectedPdfField;
	config: FieldConfig | undefined;
	isSelected: boolean;
	selectedPosition: number | null;
	totalFieldsCount: number;
	onSelect: () => void;
	setCardRef: (fieldId: string, node: HTMLDivElement | null) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	register: UseFormRegister<any>;
	onDataTypeChange: (fieldId: string, dataType: DataType) => void;
	onRenameChange: (fieldId: string, newLabel: string) => void;
}

const FieldCard: React.FC<FieldCardProps> = ({
	field,
	config,
	isSelected,
	selectedPosition,
	totalFieldsCount,
	onSelect,
	setCardRef,
	register,
	onDataTypeChange,
	onRenameChange,
}) => {
	const dataType = config?.dataType ?? 'DO_NOT_VALIDATE';
	const renamedLabel = config?.renamedLabel ?? field.fieldName;
	const isUnassigned = dataType === 'DO_NOT_VALIDATE';
	const isValidationType = DATA_TYPE_GROUPS.some(
		(group) => group.helperText && group.values.includes(dataType)
	);
	const [isEditing, setIsEditing] = React.useState(false);

	return (
		<div
			ref={(node) => setCardRef(field.id, node)}
			onClick={onSelect}
			onFocus={() => !isSelected && onSelect()}
			role="group"
			aria-label={renamedLabel}
			aria-current={isSelected ? 'true' : undefined}
			className="flex flex-col cursor-pointer"
			style={{
				background: isSelected ? '#EBDFFA' : '#F9F9F9',
				border: '1px solid rgba(13, 6, 22, 0.05)',
				borderRadius: 10,
				padding: 10,
				gap: 6,
			}}
		>
			{isEditing ? (
				<InputText
					name={field.id}
					register={register}
					rules={{
						onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
							onRenameChange(field.id, e.target.value),
					}}
					defaultValue={renamedLabel}
					autoFocus
					onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
					onBlur={() => setIsEditing(false)}
					onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
						if (e.key === 'Enter') e.currentTarget.blur();
					}}
					onClick={(e: React.MouseEvent<HTMLInputElement>) =>
						e.stopPropagation()
					}
					className="!mt-0"
					classNameInput="!mt-0 !h-[32px] !py-0 !px-4 !text-[12px] font-semibold !rounded-[12px] !border-2 !border-[#8039DF]"
				/>
			) : (
				<div className="flex items-center justify-between gap-2">
					<Typography
						type="semibold-12"
						className={clsx(
							'truncate',
							isSelected ? '!text-[#8039DF]' : '!text-[#0D0616]'
						)}
					>
						{renamedLabel}
					</Typography>
					<div className="flex items-center gap-1.5 shrink-0">
						{isSelected && selectedPosition !== null && (
							<span
								className="shrink-0 rounded-full bg-white"
								style={{ padding: '4px 10px' }}
							>
								<Typography
									type="semibold-12"
									className="!text-[11px] !text-[#8039DF] tabular-nums"
								>
									{selectedPosition}/{totalFieldsCount}
								</Typography>
							</span>
						)}
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								onSelect();
								setIsEditing(true);
							}}
							className="shrink-0 p-1"
							aria-label="Rename field"
						>
							<Icon
								src={Icons.edit}
								width={13}
								height={13}
								className={isSelected ? '[&_path]:stroke-[#8039DF]' : undefined}
							/>
						</button>
					</div>
				</div>
			)}

			<Dropdown
				className="w-full relative"
				showChevronDownIcon={false}
				classNameButton={clsx(
					'!w-full !h-[38px] justify-between !rounded-full !border-2 !px-4 !gap-2 bg-white',
					isSelected ? '!border-[#8039DF]' : 'border-[rgba(13,6,22,0.15)]'
				)}
				classNameButtonActive="!border-[#8039DF]"
				classNameMenuItems="!min-w-full !w-[219px] !rounded-3xl !shadow-[0px_14px_42px_0px_rgba(8,15,52,0.06)] bg-white p-4 max-h-[316px] overflow-y-auto"
				buttonContent={
					<span className="flex items-center justify-between w-full gap-2">
						<span className="sr-only">Data type for {renamedLabel}:</span>
						<Typography
							type="regular-12"
							className={clsx(
								'truncate text-left',
								isUnassigned ? 'text-[#6E6A73]' : 'text-[#0D0616]'
							)}
						>
							{DATA_TYPE_LABELS[dataType]}
						</Typography>
						<span className="flex items-center gap-1.5 shrink-0">
							{isValidationType && (
								<span
									className="shrink-0 flex items-center justify-center rounded-full bg-[#EAF2FF]"
									style={{ width: 18, height: 18 }}
								>
									<Icon
										src={Icons.checkCircle}
										width={12}
										height={12}
										className="text-[#2E90FA]"
									/>
								</span>
							)}
							<ChevronDownIcon className="size-4 shrink-0 text-[#6E6A73]" />
						</span>
					</span>
				}
				customItems={
					<div className="flex flex-col gap-2.5">
						{DATA_TYPE_GROUPS.map((group, groupIndex) => (
							<div
								key={group.title}
								className={clsx(
									'flex flex-col gap-2.5',
									groupIndex > 0 && 'pt-2.5'
								)}
							>
								<div className="flex items-center gap-1.5">
									{group.helperText && (
										<Icon
											src={Icons.checkCircle}
											width={14}
											height={14}
											className="shrink-0 text-[#2E90FA]"
										/>
									)}
									<Typography
										type="semibold-12"
										className="!text-[10px] uppercase tracking-[0.025em] !text-[#97949A] shrink-0"
									>
										{group.title}
									</Typography>
									<div className="flex-1 border-b border-[rgba(13,6,22,0.06)]" />
								</div>

								{group.helperText && (
									<Typography
										type="regular-12"
										className="!text-[11px] !text-[#6E6A73] text-left"
									>
										{group.helperText}
									</Typography>
								)}

								<div className="flex flex-col gap-1">
									{group.values.map((value) => (
										<Menu.Item key={value} as="div">
											<button
												type="button"
												onClick={() => onDataTypeChange(field.id, value)}
												className={clsx(
													'flex items-center justify-between gap-2 w-full py-2 px-2 rounded-lg text-left hover:bg-[#F4F4F4]',
													value === dataType && '!bg-[#EBDFFA]'
												)}
											>
												<Typography
													type="regular-12"
													className={
														value === dataType
															? '!text-[#8039DF]'
															: '!text-[#0D0616]'
													}
												>
													{DATA_TYPE_LABELS[value]}
												</Typography>
												{group.helperText && (
													<span
														className="shrink-0 flex items-center justify-center rounded-full bg-[#EAF2FF]"
														style={{ width: 18, height: 18 }}
													>
														<Icon
															src={Icons.checkCircle}
															width={12}
															height={12}
															className="text-[#2E90FA]"
														/>
													</span>
												)}
											</button>
										</Menu.Item>
									))}
								</div>
							</div>
						))}
					</div>
				}
			/>
		</div>
	);
};

export default FieldsPanel;
